import { convertCase, regionCodeToRegion } from '../logic/Address';
import { Address } from '../types/address';
import { Place } from '../types/place';

export const RESIDENTIAL = 'residential';
export const BUSINESS = 'business';

function hasChanged(street: string, place: Place, address: Address) {
  return (
    address.street?.[0] !== street ||
    address.postcode !== place.postcode ||
    address.suburb !== convertCase(place.suburb) ||
    address.region_code !== place.state_code ||
    address.country_code !== place.country_code.toUpperCase()
  );
}

class AddressStore {
  public isValid: boolean | null = null;

  shippingAddress: Address | null = null;

  billingAddress: Address | null = null;

  set(
    isValid: boolean,
    shippingAddress: Address | null,
    billingAddress: Address | null
  ) {
    this.isValid = isValid;
    this.shippingAddress = shippingAddress;
    this.billingAddress = billingAddress;
  }

  setShippingAddress({
    place,
    street,
    firstname,
    lastname,
    phone,
    address_type = '',
    instructions = '',
  }: {
    place: Place;
    street: string;
    firstname?: string;
    lastname?: string;
    phone?: string;
    address_type?: string;
    instructions?: string;
  }) {
    const address = this.shippingAddress || this.billingAddress;
    if (!address || !address.street) {
      throw new Error('shipping address should be assigned but is not');
    }

    this.shippingAddress = this.shippingAddress || {};
    const changed =
      !this.shippingAddress || hasChanged(street, place, this.shippingAddress);
    this.shippingAddress.firstname =
      firstname ||
      this.shippingAddress.firstname ||
      this.billingAddress?.firstname;
    this.shippingAddress.lastname =
      lastname ||
      this.shippingAddress.lastname ||
      this.billingAddress?.lastname;
    this.shippingAddress.street = [street];
    this.shippingAddress.suburb = convertCase(place.suburb);
    this.shippingAddress.postcode = place.postcode;
    this.shippingAddress.region_code = place.state_code;
    this.shippingAddress.region = regionCodeToRegion(place.state_code);
    this.shippingAddress.country_code = place.country_code.toUpperCase();
    this.shippingAddress.address_type = address_type;
    this.shippingAddress.instructions = instructions;
    this.shippingAddress.phone =
      phone || this.shippingAddress.phone || this.billingAddress?.phone;
    this.isValid = true;
    return changed;
  }

  getShippingAddress() {
    return this.shippingAddress || this.billingAddress!;
  }

  isValidShippingAddress() {
    return this.isValid;
  }

  isBusinessAddress() {
    if (!this.shippingAddress) {
      throw new Error('shipping address should be assigned but is not');
    }

    return this.shippingAddress.address_type === BUSINESS;
  }

  isInstructionsPresent() {
    if (!this.shippingAddress) {
      throw new Error('shipping address should be assigned but is not');
    }

    return !!(
      this.shippingAddress.instructions &&
      this.shippingAddress.instructions.length > 0
    );
  }
}

export default AddressStore;
